import { useState, useEffect } from 'react';
import axios from 'axios';
import { Sun, CloudRain, Umbrella, Wind, Droplets, ChevronRight } from 'lucide-react';
import { useLocation } from '../context/LocationContext';
import { useLanguage } from '../context/LanguageContext';
import { API } from '../lib/api';

export default function WeatherWidget() {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForecast, setShowForecast] = useState(false);
  const { location, locationData } = useLocation();
  const { language } = useLanguage();

  const translations = {
    en: {
      loading: "Checking weather...",
      beachDay: "Beach Day",
      notIdeal: "Not ideal for beach",
      humidity: "Humidity",
      wind: "Wind",
      forecast: "5-Day Forecast",
      today: "Today"
    },
    it: {
      loading: "Controllo meteo...",
      beachDay: "Giornata di Mare",
      notIdeal: "Non ideale per la spiaggia",
      humidity: "Umidità",
      wind: "Vento",
      forecast: "Previsioni 5 Giorni",
      today: "Oggi"
    },
    fr: {
      loading: "Vérification météo...",
      beachDay: "Jour de Plage",
      notIdeal: "Pas idéal pour la plage",
      humidity: "Humidité",
      wind: "Vent",
      forecast: "Prévisions 5 Jours",
      today: "Aujourd'hui"
    },
    de: {
      loading: "Wetter wird geprüft...",
      beachDay: "Strandtag",
      notIdeal: "Nicht ideal für den Strand",
      humidity: "Luftfeuchtigkeit",
      wind: "Wind",
      forecast: "5-Tage-Vorhersage",
      today: "Heute"
    }
  };

  const txt = translations[language] || translations.en;

  useEffect(() => {
    fetchWeather();
  }, [location]);

  const fetchWeather = async () => {
    try {
      const res = await axios.get(`${API}/weather?location=${location || 'monopoli'}`);
      setWeather(res.data);
    } catch (error) {
      console.error('Weather fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDay = (dateStr, index) => {
    if (index === 0) return txt.today;
    const date = new Date(dateStr);
    const days = {
      en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
      it: ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'],
      fr: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'],
      de: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa']
    };
    return (days[language] || days.en)[date.getDay()];
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-r from-sky-500 to-blue-600 rounded-2xl p-4 text-white animate-pulse">
        <p className="text-sm opacity-80">{txt.loading}</p>
      </div>
    );
  }

  if (!weather) return null;

  return (
    <div 
      className={`rounded-2xl p-4 text-white transition-all shadow-lg ${
        weather.is_beach_day 
          ? 'bg-gradient-to-br from-amber-400 via-terracotta-400 to-terracotta-500 shadow-terracotta-500/20' 
          : 'bg-gradient-to-br from-sea-600 to-sea-700 shadow-sea-700/20'
      }`}
      data-testid="weather-widget"
    >
      {/* Current Weather */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-4xl">{weather.condition_icon}</span>
            <div>
              <p className="text-3xl font-bold">{Math.round(weather.temperature)}°</p>
              <p className="text-sm opacity-90">{weather.condition}</p>
            </div>
          </div>
          <p className="text-xs mt-2 opacity-80">{locationData?.name || weather.location}</p>
        </div>
        
        <div className="text-right">
          <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${
            weather.is_beach_day ? 'bg-white/30' : 'bg-white/20'
          }`}>
            {weather.is_beach_day ? <Sun size={14} /> : <CloudRain size={14} />}
            {weather.is_beach_day ? txt.beachDay : txt.notIdeal}
          </div>
        </div>
      </div>

      {/* Beach Recommendation */}
      <p className="mt-3 text-sm bg-white/20 rounded-xl p-3">
        {weather.beach_recommendation}
      </p>

      {/* Weather Details */}
      <div className="flex gap-4 mt-3 text-sm">
        <div className="flex items-center gap-1.5 opacity-80">
          <Droplets size={14} />
          <span>{txt.humidity}: {weather.humidity}%</span>
        </div>
        <div className="flex items-center gap-1.5 opacity-80">
          <Wind size={14} />
          <span>{txt.wind}: {Math.round(weather.wind_speed)} km/h</span>
        </div>
      </div>

      {/* 5-Day Forecast Toggle */}
      {weather.forecast && weather.forecast.length > 0 && (
        <>
          <button
            onClick={() => setShowForecast(!showForecast)}
            className="w-full mt-3 flex items-center justify-between py-2 text-sm font-medium opacity-90 hover:opacity-100 transition-opacity"
          >
            <span>{txt.forecast}</span>
            <ChevronRight size={16} className={`transition-transform ${showForecast ? 'rotate-90' : ''}`} />
          </button>

          {showForecast && (
            <div className="grid grid-cols-5 gap-2 mt-2">
              {weather.forecast.map((day, i) => (
                <div 
                  key={day.date} 
                  className={`text-center p-2 rounded-xl ${i === 0 ? 'bg-white/30' : 'bg-white/10'}`}
                >
                  <p className="text-xs font-medium">{formatDay(day.date, i)}</p>
                  <p className="text-lg my-1">{day.icon}</p>
                  <p className="text-xs">
                    <span className="font-medium">{Math.round(day.temp_max)}°</span>
                    <span className="opacity-70"> / {Math.round(day.temp_min)}°</span>
                  </p>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
